import { slideRegistry } from '../core/plugin-system';
import { createDefaultCompositeValidator } from '../core/plugin-system/PluginValidator';

/**
 * Initialize and configure the slide plugin system
 */
export function initializePluginSystem(): void {
  // Add default validators
  const validator = createDefaultCompositeValidator();
  slideRegistry.addValidator(validator);

  // Initialize the registry
  slideRegistry.initialize();

  console.log('📚 Plugin system initialized');
}

/**
 * Register all slide plugins
 * This will be called after individual plugins are imported
 */
export async function registerAllPlugins(): Promise<void> {
  try {
    // Dynamically import and register plugins
    // This approach allows for lazy loading and better code splitting

    const pluginModules = await Promise.all([
      import('../plugins/question/QuestionSlidePlugin'),
      import('../plugins/image-upload/ImageUploadSlidePlugin'),
      import('../plugins/text/TextSlidePlugin'),
      import('../plugins/video/VideoSlidePlugin'),
      import('../plugins/info/InfoSlidePlugin'),
    ]);

    // Register each plugin (with guard to prevent duplicate registration on component remounts)
    pluginModules.forEach((module) => {
      if (module.default) {
        // Check if plugin already registered before attempting to register
        if (!slideRegistry.has(module.default.type)) {
          slideRegistry.register(module.default);
        }
      }
    });

    console.log('✅ All slide plugins registered successfully');
    console.log('📊 Plugin statistics:', slideRegistry.getStats());
  } catch (error: unknown) {
    console.error('❌ Failed to register plugins:', error);
    throw error;
  }
}

/**
 * Get plugin configuration for a lesson type
 */
export function getPluginsForLessonType(lessonType: string): string[] {
  // Import the lesson type configuration
  const { getAllowedSlideTypes } = require('./lesson-types.config');
  return getAllowedSlideTypes(lessonType);
}

/**
 * Validate if all required plugins are registered
 */
export function validateRequiredPlugins(): boolean {
  const requiredPlugins = [
    'QUESTION',
    'IMAGE_UPLOAD',
    'TEXT',
    'VIDEO_PREVIEW',
    'INFO',
  ];

  const missingPlugins = requiredPlugins.filter(
    (type) => !slideRegistry.has(type)
  );

  if (missingPlugins.length > 0) {
    console.error('❌ Missing required plugins:', missingPlugins);
    return false;
  }

  console.log('✅ All required plugins are registered');
  return true;
}

/**
 * Get plugin display information for UI
 */
export interface PluginDisplayInfo {
  type: string;
  displayName: string;
  description: string;
  icon: React.ComponentType<any>;
  available: boolean;
}

export function getAvailablePlugins(lessonType?: string): PluginDisplayInfo[] {
  const allPlugins = slideRegistry.getAll();
  let allowedTypes: string[] | undefined;

  if (lessonType) {
    allowedTypes = getPluginsForLessonType(lessonType);
  }

  return allPlugins
    .filter((plugin) => !allowedTypes || allowedTypes.includes(plugin.type))
    .map((plugin) => ({
      type: plugin.type,
      displayName: plugin.displayName,
      description: plugin.description,
      icon: plugin.icon,
      available: true,
    }));
}

/**
 * Plugin system health check
 */
export function performHealthCheck(): {
  isHealthy: boolean;
  details: {
    initialized: boolean;
    pluginCount: number;
    requiredPluginsPresent: boolean;
    stats: ReturnType<typeof slideRegistry.getStats>;
  };
} {
  const initialized = slideRegistry.isInitialized();
  const stats = slideRegistry.getStats();
  const requiredPluginsPresent = validateRequiredPlugins();

  return {
    isHealthy: initialized && requiredPluginsPresent && stats.totalPlugins > 0,
    details: {
      initialized,
      pluginCount: stats.totalPlugins,
      requiredPluginsPresent,
      stats,
    },
  };
}

/**
 * Reset plugin system (useful for testing)
 */
export function resetPluginSystem(): void {
  slideRegistry.clear();
  console.log('🔄 Plugin system reset');
}

/**
 * Export registry for direct access if needed
 */
export { slideRegistry } from '../core/plugin-system';
